import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, AlertTriangle, Sparkles, Loader2, FileCode } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const CELL_CLASS = {
  critical: "bg-[color:var(--critical)] text-white",
  high: "bg-[color:var(--critical)]/25 text-[color:var(--critical)] border border-[color:var(--critical)]/40",
  medium: "bg-[color:var(--warning)]/20 text-[color:var(--warning)] border border-[color:var(--warning)]/40",
  low: "bg-[color:var(--accent)]/15 text-[color:var(--accent)] border border-[color:var(--accent)]/40",
};
const CELL_LABEL = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };

const SEV_DOT = {
  critical: "bg-[color:var(--critical)] shadow-[0_0_8px_var(--critical)]",
  high: "bg-[color:var(--warning)]",
  medium: "bg-[color:var(--accent)]",
  low: "bg-[color:var(--text-2)]",
};

const CATEGORY_LABEL = {
  exposure: "Security Exposure",
  debt: "Tech Debt",
  biz: "Business Criticality",
  compliance: "Compliance Gap",
};

const COUNT_BADGE_CLASS = {
  critical: "bg-[color:var(--critical)]/15 text-[color:var(--critical)] border-[color:var(--critical)]/50 hover:bg-[color:var(--critical)]/25",
  high: "bg-[color:var(--warning)]/15 text-[color:var(--warning)] border-[color:var(--warning)]/50 hover:bg-[color:var(--warning)]/25",
  medium: "bg-[color:var(--accent)]/15 text-[color:var(--accent)] border-[color:var(--accent)]/50 hover:bg-[color:var(--accent)]/25",
  low: "bg-[color:var(--text-2)]/10 text-[color:var(--text-2)] border-[color:var(--text-2)]/40 hover:bg-[color:var(--text-2)]/20",
};

export default function Diagnose() {
  const [state, setState] = useState({
    heatmap: [],
    findings: [],
    counts: { critical: 0, high: 0, medium: 0, low: 0 },
    blast_radius: { core: null, satellites: [] },
  });
  const [highlight, setHighlight] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", subtitle: "", items: [] });

  const load = async () => {
    const { data } = await api.get("/api/diagnose/state");
    setState({
      counts: { critical: 0, high: 0, medium: 0, low: 0 },
      ...data,
    });
  };
  useEffect(() => { load(); }, []);

  const reset = async () => {
    if (!window.confirm("Reset diagnose findings & scores for this workspace?")) return;
    try {
      await api.delete("/api/diagnose/reset");
      await load();
      toast.success("Diagnose reset");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post("/api/diagnose/analyze");
      toast.success(`AI scored ${data.systems_scored} systems · ${data.findings} findings`);
      await load();
    } catch (e) {
      const detail = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      toast.error(detail);
    } finally {
      setAnalyzing(false);
    }
  };

  const counts = state.counts || { critical: 0, high: 0, medium: 0, low: 0 };

  const openBySeverity = (sev) => {
    const items = state.findings.filter((f) => f.sev === sev);
    if (items.length === 0) {
      toast.message(`No ${CELL_LABEL[sev] || sev} findings.`);
      return;
    }
    setModal({
      open: true,
      title: `${CELL_LABEL[sev] || sev} Findings`,
      subtitle: `${items.length} issue${items.length === 1 ? "" : "s"} at ${CELL_LABEL[sev] || sev} severity`,
      items,
    });
  };

  const openByCell = (row, category) => {
    const items = state.findings.filter(
      (f) => f.system_id === row.id && f.category === category
    );
    if (items.length === 0) {
      toast.message(`No specific findings tied to ${CATEGORY_LABEL[category]} for ${row.name}.`);
      return;
    }
    setModal({
      open: true,
      title: `${row.name} · ${CATEGORY_LABEL[category]}`,
      subtitle: `${items.length} issue${items.length === 1 ? "" : "s"} · rated ${CELL_LABEL[row[category]] || "Low"}`,
      items,
    });
  };

  return (
    <div className="p-6 md:p-10 space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">/ stage · 02 — diagnose</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight">
            Risk & Security Intelligence
          </h1>
          <p className="mt-2 text-[color:var(--text-2)] max-w-2xl">
            Every scanned system is scored across security exposure, technical
            debt, business criticality and compliance gaps — so modernization
            spend follows actual risk, not guesswork.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="ai-analyze-btn"
            onClick={analyze}
            disabled={analyzing || state.heatmap.length === 0}
            className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black h-11 px-4 hover:bg-[color:var(--accent-hover)] transition-colors duration-150 mono text-xs uppercase tracking-widest disabled:opacity-50"
          >
            {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {analyzing ? "Analyzing…" : "AI Deep-Analysis"}
          </button>
          <button
            data-testid="reset-diagnose-btn"
            onClick={reset}
            className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-11 px-4 hover:border-[color:var(--critical)] hover:text-[color:var(--critical)] transition-colors duration-150 mono text-xs uppercase tracking-widest"
          >
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      </div>

      {/* Portfolio Risk Heatmap */}
      <section className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-lg font-medium tracking-tight">Portfolio Risk Heatmap</div>
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            Click a cell to inspect issues
          </div>
        </div>
        {state.heatmap.length === 0 ? (
          <div className="mono text-xs text-[color:var(--text-3)] p-4">
            No systems scanned yet. Run a Scan first.
          </div>
        ) : (
          <table className="w-full mono text-xs" data-testid="heatmap-table">
            <thead>
              <tr className="text-[color:var(--text-3)]">
                <th className="text-left p-3 uppercase tracking-widest font-normal">System</th>
                <th className="text-left p-3 uppercase tracking-widest font-normal">Security Exposure</th>
                <th className="text-left p-3 uppercase tracking-widest font-normal">Tech Debt</th>
                <th className="text-left p-3 uppercase tracking-widest font-normal">Business Criticality</th>
                <th className="text-left p-3 uppercase tracking-widest font-normal">Compliance Gap</th>
              </tr>
            </thead>
            <tbody>
              {state.heatmap.map((r) => (
                <tr key={r.id} className="border-t border-[color:var(--border)]">
                  <td className="p-3 text-white text-sm font-medium">{r.name}</td>
                  {["exposure", "debt", "biz", "compliance"].map((k) => (
                    <td key={k} className="p-3">
                      <button
                        type="button"
                        data-testid={`heatmap-cell-${r.id}-${k}`}
                        onClick={() => openByCell(r, k)}
                        className={`inline-flex px-3 py-1.5 mono text-xs font-semibold cursor-pointer hover:opacity-90 hover:ring-1 hover:ring-white/40 transition ${CELL_CLASS[r[k]] || CELL_CLASS.low}`}
                      >
                        {CELL_LABEL[r[k]] || "Low"}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Findings + Blast Radius */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4">
        <section className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
          <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
            <div className="text-lg font-medium tracking-tight">Active Findings</div>
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
              Click a count to inspect
            </div>
          </div>

          {/* Clickable severity count badges */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="severity-counts">
            {["critical", "high", "medium", "low"].map((sev) => (
              <button
                key={sev}
                type="button"
                data-testid={`count-badge-${sev}`}
                onClick={() => openBySeverity(sev)}
                disabled={!counts[sev]}
                className={`inline-flex items-center gap-2 border px-3 py-1.5 mono text-[11px] uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${COUNT_BADGE_CLASS[sev]}`}
              >
                <span className="text-sm font-semibold tabular-nums">{counts[sev] || 0}</span>
                <span>{CELL_LABEL[sev]}</span>
              </button>
            ))}
          </div>

          {state.findings.length === 0 ? (
            <div className="mono text-xs text-[color:var(--text-3)] p-4">
              No findings — clean estate.
            </div>
          ) : (
            <div className="divide-y divide-[color:var(--border)]">
              {state.findings.map((f, i) => (
                <div key={i} data-testid={`finding-${i}`} className="flex gap-3 items-start py-3">
                  <div className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${SEV_DOT[f.sev] || SEV_DOT.medium}`} />
                  <div className="min-w-0">
                    <div className="text-sm text-white">{f.title}</div>
                    <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mt-1">
                      {f.meta}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-lg font-medium tracking-tight">Blast Radius</div>
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
              Click the core node
            </div>
          </div>
          <BlastRadius
            core={state.blast_radius.core}
            satellites={state.blast_radius.satellites}
            highlight={highlight}
            onCoreClick={() => {
              setHighlight(true);
              setTimeout(() => setHighlight(false), 1600);
            }}
          />
        </section>
      </div>

      {/* Issue-list modal */}
      <Dialog open={modal.open} onOpenChange={(v) => setModal((m) => ({ ...m, open: v }))}>
        <DialogContent
          data-testid="issues-modal"
          className="max-w-2xl bg-[color:var(--bg-1)] border border-[color:var(--border-strong)] text-white"
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-medium tracking-tight" data-testid="issues-modal-title">
              {modal.title}
            </DialogTitle>
            <DialogDescription className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
              {modal.subtitle}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-[color:var(--border)]">
            {modal.items.map((f, i) => (
              <div
                key={i}
                data-testid={`issue-row-${i}`}
                className="flex gap-3 items-start py-3"
              >
                <div className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${SEV_DOT[f.sev] || SEV_DOT.medium}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white">{f.title}</div>
                  <div className="mono text-[11px] text-[color:var(--text-2)] mt-1 flex items-center gap-1.5 break-all">
                    <FileCode size={11} className="shrink-0 text-[color:var(--accent)]" />
                    <span data-testid={`issue-path-${i}`}>{f.file_path || "—"}</span>
                  </div>
                  <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mt-1">
                    {f.meta}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BlastRadius({ core, satellites, highlight, onCoreClick }) {
  if (!core) {
    return (
      <div className="mono text-xs text-[color:var(--text-3)] p-4">
        Scan first to see interdependencies.
      </div>
    );
  }
  const w = 320, h = 260;
  const cx = w / 2, cy = h / 2 - 10;
  const r = 90;
  const sats = satellites.slice(0, 6);
  const n = Math.max(sats.length, 1);
  return (
    <div className="flex justify-center py-2">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} data-testid="blast-radius-svg">
        {sats.map((s, i) => {
          const angle = (2 * Math.PI * i) / n - Math.PI / 2;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r;
          const stroke = highlight ? "var(--critical)" : "var(--border)";
          const strokeW = highlight ? 2.5 : 1.5;
          return (
            <g key={s.id}>
              <line className="net-edge" x1={cx} y1={cy} x2={x} y2={y} stroke={stroke} strokeWidth={strokeW} />
              <circle cx={x} cy={y} r="10" fill="var(--bg-1)" stroke="var(--info)" strokeWidth="1.5" />
              <text x={x} y={y + 24} fill="var(--text-2)" fontSize="9.5" fontFamily="JetBrains Mono, monospace" textAnchor="middle">
                {s.name.length > 14 ? s.name.slice(0, 13) + "…" : s.name}
              </text>
            </g>
          );
        })}
        <circle
          cx={cx} cy={cy} r="19"
          fill="var(--bg-1)" stroke="var(--accent)" strokeWidth="2"
          style={{ cursor: "pointer" }}
          onClick={onCoreClick}
          data-testid="blast-core"
        />
        <text x={cx} y={cy + 4} fill="var(--accent)" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle" style={{ pointerEvents: "none" }}>
          {core.name.length > 8 ? core.name.slice(0, 7).toUpperCase() : core.name.toUpperCase()}
        </text>
      </svg>
    </div>
  );
}
